import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Box, CircularProgress } from "@mui/material";
import { ChevronLeft } from "@mui/icons-material";
import { useSelector } from "react-redux";
import * as Sentry from "@sentry/react";
import { Button } from "./Button";
import { ApiError, ExpedienteTabItem } from "@interfaces/response";
import { BOFU_TABS_CONFIG, PATHS_EXPEDIENTE_V2, STATUS_CATALOG } from "@helpers/constantes";
import { BOFU_TAB_ORDER } from "@components/TabBofu/functions";
import { RootState, useAppDispatch } from "@store/store";
import { setModalErrorVisible } from "@store/slices/appSlice";
import { usePostValidateNextStageMutation } from "@api/applications";
import useStatusConfig from "@hooks/useStatusConfig";

const BASE = PATHS_EXPEDIENTE_V2.expediente.path;

interface TabEntry {
  code: string;
  queryParam: string;
  backLabel: string;
  nextLabel: string;
  tab: ExpedienteTabItem;
}

interface Props {
  currentTab: ExpedienteTabItem["code"];
  nextDisabled?: boolean;
  showRevision?: boolean;
}

export const StepActionsExpediente = ({ currentTab, nextDisabled = false, showRevision = false }: Props) => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const expediente = useSelector((state: RootState) => state.expediente);
  const application = useSelector((state: RootState) => state.register.application);
  const accountUser = useSelector((state: RootState) => state.register.account_user);

  const { nextStatus } = useStatusConfig(STATUS_CATALOG.avance_expediente_digital.code);
  const [triggerPostNextStage] = usePostValidateNextStageMutation();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const orderedTabs: TabEntry[] = BOFU_TAB_ORDER.map(({ queryParam, code, backLabel, nextLabel }) => ({
    code,
    queryParam,
    backLabel,
    nextLabel,
    tab: expediente[queryParam],
  }));

  const currentIndex = orderedTabs.findIndex((entry) => entry.code === currentTab);
  const prevEntry = orderedTabs
    .slice(0, currentIndex)
    .reverse()
    .find((entry) => entry.tab.is_active);
  const nextEntry = orderedTabs.slice(currentIndex + 1).find((entry) => entry.tab.is_active);

  const goTo = (queryParam: string) => navigate(`${BASE}?tab=${queryParam}`);

  const handlePostDictamen = async (): Promise<boolean> => {
    if (application.status_catalog?.code !== STATUS_CATALOG.documentos_dictamen.code) return true;

    try {
      setIsSubmitting(true);
      await triggerPostNextStage({
        applicationId: application.id,
        statusCode: STATUS_CATALOG.dictamen_juridico.code,
        username: `BOFU - ${accountUser.email}`,
      }).unwrap();
      return true;
    } catch (error) {
      Sentry.captureException(error, { extra: { component: "StepActionsExpediente-dictamenToEmpresa" } });
      dispatch(setModalErrorVisible({ open: true, error: error as ApiError }));
      return false;
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleRevision = async () => {
    try {
      setIsSubmitting(true);

      if (!nextStatus) {
        throw new Error("No se encontró el siguiente estatus");
      }

      await triggerPostNextStage({
        applicationId: application.id,
        statusCode: nextStatus.code,
        username: `BOFU - ${accountUser.email}`,
      }).unwrap();

      navigate(nextStatus.url || "");
    } catch (error) {
      Sentry.captureException(error, { extra: { component: "StepActionsExpediente-revision" } });
      dispatch(setModalErrorVisible({ open: true, error: error as ApiError }));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleNext = async (entry: TabEntry) => {
    const isDictamenToEmpresa =
      currentTab === BOFU_TABS_CONFIG.tab_dictamen.code && entry.code === BOFU_TABS_CONFIG.tab_empresa.code;

    if (isDictamenToEmpresa) {
      const ok = await handlePostDictamen();
      if (!ok) return;
    }

    goTo(entry.queryParam);
  };

  return (
    <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mt: 4, gap: 2 }}>
      {prevEntry ? (
        <Button
          tone="ghost"
          startIcon={<ChevronLeft fontSize="small" />}
          onClick={() => goTo(prevEntry.queryParam)}
          disabled={isSubmitting}
        >
          {prevEntry.backLabel}
        </Button>
      ) : (
        <Box />
      )}

      <Box sx={{ display: "flex", gap: 2 }}>
        {nextEntry && (
          <Button
            tone="primary"
            onClick={() => handleNext(nextEntry)}
            disabled={nextEntry.tab.is_disabled || nextDisabled || isSubmitting}
            startIcon={isSubmitting ? <CircularProgress size={14} color="inherit" /> : undefined}
            withArrow
          >
            {nextEntry.nextLabel}
          </Button>
        )}

        {showRevision && (
          <Button
            tone="primary"
            onClick={handleRevision}
            disabled={isSubmitting || nextDisabled}
            startIcon={isSubmitting ? <CircularProgress size={14} color="inherit" /> : undefined}
          >
            Enviar el expediente a revisión
          </Button>
        )}
      </Box>
    </Box>
  );
};
